import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Apollo, gql } from 'apollo-angular';
import { FullUserFragment } from 'graphql.generated';
import { DialogService } from 'primeng/dynamicdialog';

import { combineLatest, filter, from, map } from 'rxjs';
import { JobCreateCustomerActions } from 'src/app/job/job-create/job-create-customer/job-create-customer.actions';
import { SubSink } from 'subsink';

import { HistoryService } from '../../history/history.service';

import { BrandingService } from '../../services/branding.service';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { FreyaMutateService } from '../../services/freya-mutate.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { PermissionService } from '../../services/permission.service';
import { DisabledWhen, KarveMenuItem, setMenuItemDisabled } from '../../utilities/menu-item.util';
import { ChangeUserPasswordComponent } from '../change-user-password/change-user-password.component';
import { DEACTIVATE_USER_REFRESH_TOKENS_MUTATION, SEND_RESET_PASSWORD_MUTATION } from '../mutate-user/mutate-user.queries';

@Component({
  selector: 'app-user-actions',
  templateUrl: './user-actions.component.html',
  styleUrls: ['./user-actions.component.scss']
})
export class UserActionsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() user: FullUserFragment;
  @Input() disabled: boolean;

  canDelete = false;
  canRestore = false;

  subs = new SubSink();

  editUserAction: KarveMenuItem = {
    label: 'Edit',
    icon: 'pi pi-pencil',
    command: () => {
      this.freyaMutate.openMutateObject({
        mutateType: 'update',
        objectType: 'user',
        object: this.user,
      });
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
    }
  };

  deleteUserAction: KarveMenuItem = {
    label: 'Delete',
    icon: 'pi pi-trash',
    command: () => {
      this.freyaMutate.openDeleteObject({
        objectId: this.user.id,
        objectName: this.userName,
        objectType: 'user',
      });
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
    }
  };

  bookJobAction: KarveMenuItem = {
    label: 'Book Job',
    icon: 'pi pi-money-bill',
    command: () => {
      this.openJobCreationTool();
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
    },
  };

  changePasswordUserAction: KarveMenuItem = {
    label: 'Change password',
    icon: 'pi pi-key',
    command: () => {
      this.openEditUserPasswordDialog();
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
    }
  };

  lockUserAction: KarveMenuItem = {
    label: 'Lock account',
    icon: 'pi pi-lock',
    command: () => {
      this.toggleLock(true);
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
    }
  };

  unlockUserAction: KarveMenuItem = {
    label: 'Unlock account',
    icon: 'pi pi-unlock',
    command: () => {
      this.toggleLock(false);
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
    }
  };

  restoreUserAction: KarveMenuItem = {
    label: 'Restore',
    icon: 'pi pi-replay',
    command: () => {
      this.restoreUser();
    },
    visible: false,
  };

  sendResetPasswordAction: KarveMenuItem = {
    label: 'Send reset password email',
    icon: 'pi pi-envelope',
    command: () => {
      this.sendResetPassword();
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
      userHasNoEmail: true,
    }
  };

  sendLoginLinkAction: KarveMenuItem = {
    label: 'Send login link',
    icon: 'pi pi-sign-in',
    command: () => {
      this.sendLoginLink();
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
      userHasNoEmail: true,
    }
  };

  signOutEverywhereAction: KarveMenuItem = {
    label: 'Sign out of all devices',
    icon: 'pi pi-sign-out',
    command: () => {
      this.signOutEverywhere();
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
    }
  };

  viewHistoryAction = {
    label: 'View History',
    icon: 'pi pi-book',
    command: () => {
      if (!this?.user?.id) { return; }
      this.historyService.openHistory('User', [this.user.id]);
    }
  };

  impersonateAction: KarveMenuItem = {
    label: 'Impersonate',
    icon: 'pi pi-user-edit',
    command: () => {
      this.impersonateUser();
    },
    visible: false,
    disabledWhen: {
      objectDeleted: true,
    }
  };

  userActions = [{
    label: 'User Actions',
    items: [
      this.editUserAction,
      this.deleteUserAction,
      this.bookJobAction,
      this.changePasswordUserAction,
      this.sendResetPasswordAction,
      this.sendLoginLinkAction,
      this.signOutEverywhereAction,
      this.lockUserAction,
      this.unlockUserAction,
      this.restoreUserAction,
      this.impersonateAction,
      this.viewHistoryAction,
    ]
  }];

  constructor(
    private freyaMutate: FreyaMutateService,
    private router: Router,
    private dialogService: DialogService,
    private permissionHandler: PermissionService,
    private freyaHelper: FreyaHelperService,
    private historyService: HistoryService,
    private store: Store,
    private actions$: Actions,
    private apollo: Apollo,
    private localNotify: FreyaNotificationsService,
    private http: HttpClient,
    private branding: BrandingService,
    private detailsHelper: DetailsHelperService,
  ) { }


  ngOnInit(): void {
    this.initializePermissions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.hasOwnProperty('user')) { return; }
    this.setDisabledActions();
    this.setLockActionsVisibility();
  }

  setLockActionsVisibility() {
    this.lockUserAction.visible = this.canDelete && !this.user?.lockedAt;
    this.unlockUserAction.visible = this.canRestore && !!this.user?.lockedAt;
    this.restoreUserAction.visible = this.canRestore && !!this.user?.deletedAt;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  initializePermissions() {
    this.subs.sink = this.permissionHandler.watchPermissions([
      'users.edit',
      'users.delete',
      'users.setPassword',
      'users.restore',
      'frontend.estimating',
      'jobs.update',
      'dashboard.billing',
    ])
      .subscribe((res) => {
        this.editUserAction.visible = res[0];
        this.deleteUserAction.visible = res[1];
        this.changePasswordUserAction.visible = res[2];
        this.sendResetPasswordAction.visible = res[2];
        this.sendLoginLinkAction.visible = res[2];
        this.canDelete = res[1];
        this.canRestore = res[3];
        this.setLockActionsVisibility();
        this.bookJobAction.visible = res[4] && res[5];
        this.impersonateAction.visible = res[6];
      });

    this.subs.sink = combineLatest([
      this.permissionHandler.watchPermission('auth.deactivateRefreshTokens'),
      this.branding.currentZone(),
    ]).subscribe(([ canDeactivate, zone ]) => {
      this.signOutEverywhereAction.visible = canDeactivate && zone?.type === 'root';
    });
  }

  signOutEverywhere() {
    if (!this.user?.id) { return; }
    if (!confirm(`Sign ${this.userName} out of all devices? Devices already signed in can keep working for up to an hour.`)) { return; }
    this.subs.sink = this.apollo.mutate({
      mutation: DEACTIVATE_USER_REFRESH_TOKENS_MUTATION,
      variables: { userId: this.user.id },
    }).subscribe({
      next: () => {
        this.localNotify.success(`Signed ${this.userName} out of all devices`);
      },
      error: (err) => {
        this.localNotify.apolloError('Failed to sign user out of all devices', err);
      },
    });
  }

  openEditUserPasswordDialog() {
    this.dialogService.open(ChangeUserPasswordComponent, {
      header: 'Change Password',
      data: {
        user: this.user,
      },
      contentStyle: this.freyaHelper.getDialogContentStyle('0.5rem'),
    });
  }

  sendResetPassword() {
    if (!this.user?.id) { return; }
    this.subs.sink = this.apollo.mutate({
      mutation: SEND_RESET_PASSWORD_MUTATION,
      variables: { userId: this.user.id },
    }).subscribe({
      next: () => {
        this.localNotify.success('Password reset email sent');
      },
      error: (err) => {
        this.localNotify.apolloError('Failed to send reset password email', err);
      },
    });
  }

  sendLoginLink() {
    if (!this.user?.email) {
      this.localNotify.addToast.next({ severity: 'error', summary: 'User has no email address' });
      return;
    }
    this.subs.sink = this.http.post('/api/auth/send-login-link', { email: this.user.email }).subscribe({
      next: () => {
        this.localNotify.success(`Login link sent to ${this.user.email}`);
      },
      error: (err) => {
        this.localNotify.addToast.next({
          severity: 'error',
          summary: err?.error?.error || 'Failed to send login link',
        });
      },
    });
  }

  impersonateUser() {
    if (!this.user?.id) { return; }
    if (!confirm(`Log in as ${this.userName}? You will be redirected to their dashboard.`)) { return; }
    this.subs.sink = this.http.post('/api/auth/impersonate', { userId: this.user.id }).subscribe({
      next: (res: any) => {
        if (res?.redirectUrl) {
          window.location.href = res.redirectUrl;
        }
      },
      error: (err) => {
        this.localNotify.addToast.next({
          severity: 'error',
          summary: err?.error?.error || 'Failed to impersonate user',
        });
      },
    });
  }

  toggleLock(lock: boolean) {
    if (!this.user?.id) { return; }
    const mutation = lock
      ? gql`mutation LockUser($userIds: [String!]!) {
          lockUsers(userIds: $userIds)
        }`
      : gql`mutation UnlockUser($userIds: [String!]!) {
          unlockUsers(userIds: $userIds)
        }`;
    this.subs.sink = this.apollo.mutate({
      mutation,
      variables: { userIds: [this.user.id] },
    }).subscribe({
      next: () => {
        this.detailsHelper.pushUpdate({ id: this.user.id, type: 'User', action: 'update' });
        this.localNotify.success(lock ? 'Account locked' : 'Account unlocked');
      },
      error: (err) => {
        this.localNotify.apolloError(lock ? 'Failed to lock account' : 'Failed to unlock account', err);
      },
    });
  }

  restoreUser() {
    if (!this.user?.id) { return; }
    this.subs.sink = this.apollo.mutate({
      mutation: gql`mutation RestoreUser($userIds: [String!]!) {
        restoreUsers(userIds: $userIds)
      }`,
      variables: { userIds: [this.user.id] },
    }).subscribe({
      next: () => {
        this.detailsHelper.pushUpdate({ id: this.user.id, type: 'User', action: 'update' });
        this.localNotify.success('User restored');
      },
      error: (err) => {
        this.localNotify.apolloError('Failed to restore user', err);
      },
    });
  }

  setDisabledActions() {

    const disabledWhen: DisabledWhen = {
      objectDeleted: Boolean(this.user?.deletedAt),
      userHasNoEmail: !this.user?.email,
    };

    const [ actionsMenu ] = this.userActions;

    const { items: userActions } = actionsMenu;

    for (const action of userActions) {
      setMenuItemDisabled(action, disabledWhen);
    }

  }

  openJobCreationTool() {
    from(this.router.navigate(
      [ '/jobs', 'new' ],
      {
        queryParamsHandling: 'preserve',
        state: {
          existingCustomer: this.user,
        }
      },
    )).pipe(
      filter((navigationSuccess) => navigationSuccess),
      map(() => {

        const existingCustomer = {
          ...this.user,
          fullName: `${this.user.givenName} ${this.user.familyName}`,
        };

        return JobCreateCustomerActions.existingCustomerPreSelected({ existingCustomer });
      }),
    ).subscribe((action) => this.store.dispatch(action));
  }

  get userName() {
    return [ this.user.givenName, this.user.familyName ].filter(Boolean).join(' ');
  }
}
